import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

/**
 * FeatureFlagsContext — single source of truth for runtime feature flags.
 *
 * Fetches once on app mount from public `GET /api/feature-flags`, then exposes
 * a simple `useFeatureFlag('merch')` hook. Components return `null` when off.
 *
 * Defaults to all-OFF while loading so users never see a flagged feature
 * flicker in. Admin can flip flags from Fire Station and call refresh() to
 * pull the new state without a page reload.
 *
 * Auth detail: this endpoint is public — exposing the *existence* of flags is
 * fine; the actual content gated behind a flag still enforces its own auth.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeatureFlagsContext = createContext({
  flags: {},
  loading: true,
  refresh: async () => {},
});

export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchFlags = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feature-flags`);
      setFlags(res.data?.flags || {});
    } catch {
      // If the backend is unreachable, leave flags empty (== all OFF).
      // This is the safest fail mode: no flagged feature appears.
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, refresh: fetchFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Returns true ONLY when the flag is explicitly enabled.
 *
 * EXCEPTION: admins ALWAYS see the feature as enabled, regardless of flag
 * state. This lets the founder demo unreleased features without flipping a
 * live flag globally. Tradeoff: admin sessions will see UI flicker briefly
 * when AuthContext is still loading the user — `loading` covers this so
 * gated render sites that respect `loading` won't double-flash.
 *
 * Safe default = false for non-admins.
 */
export const useFeatureFlag = (flagId) => {
  const { flags, loading: flagsLoading } = useContext(FeatureFlagsContext);
  const { user, loading: authLoading } = useAuth();
  const isAdmin = user?.role === 'admin';
  return {
    enabled: isAdmin || !!flags?.[flagId]?.enabled,
    loading: flagsLoading || authLoading,
  };
};

/** Direct access to all flag metadata — used by the admin UI. */
export const useAllFeatureFlags = () => useContext(FeatureFlagsContext);
